/* This class is not a proxy of eventemitter3. It implements its own methods and properties.
 * Its public interface should not expose methods from the EventEmitter implementation used.
 */
import { EventEmitter } from "eventemitter3";
import { validateChannel } from "./validateChannel";
const ee = new EventEmitter();
;
export const InternalMessageBusChannelSetError = "Cannot alter readonly property [channels] of InternalMessageBus instance.";
/**
 * InternalMessageBus is an async in-memory msg bus based partially on NodeJS EventEmitter interface.
 * Supported in web browser via: https://github.com/primus/eventemitter3
 *
 * Channel is defined as a dot separated string - "channel.subChannel".
 * Listeners can be attached to both "channel" and "subChannel".
 * Channel and subChannel names are case-sensitive.
 *
 * Methods: on/once/off/emit are chainable.
 *
 */
export class InternalMessageBus {
    /**
     * @constructor
     */
    constructor() {
        Object.defineProperties(this, {
            channels: {
                get: () => ee.eventNames(),
                set: x => { throw new Error(InternalMessageBusChannelSetError); }
            }
        });
    }
    /**
     * on(...params) - attaches event listener to the specified event. To subscribe to the entire root channel you must listen for "channelName.*"
     * @param {STRING} channel
     * @param {InternalMessageBusListener} callback called with `InternalMessageBusMessage` object.
     * @param {ANY} context If you don't have access to ES6 syntax ()=>{} you can pass the optional object to be used for context.
     * @returns {InternalMessageBus} `InternalMessageBus instance`
     */
    on(channel, callback, context) {
        /* istanbul ignore next */ /* all cases covered with tests */
        if (validateChannel(channel)) {
            ee.on(channel, callback, context);
        }
        return this;
    }
    /**
     * once(...params) - attaches event listener to the specified event that happens only once. To subscribe to the entire root channel you must listen for "channelName.*"
     * @param {STRING} channel
     * @param {InternalMessageBusListener} callback called with `InternalMessageBusMessage` object.
     * @param {ANY} context If you don't have access to ES6 syntax ()=>{} you can pass the optional object to be used for context.
     * @returns {InternalMessageBus} `InternalMessageBus instance`
     */
    once(channel, callback, context) {
        /* istanbul ignore next */ /* all cases covered with tests */
        if (validateChannel(channel)) {
            ee.once(channel, callback, context);
        }
        return this;
    }
    /**
     * off(...params) - detaches event listener from the specified event. To unsubscribe to the entire root channel you must listen for "channelName.*"
     * @param {STRING} channel
     * @param {InternalMessageBusListener} callback called with `InternalMessageBusMessage` object.
     * @param {ANY} context If you don't have access to ES6 syntax ()=>{} you can pass the optional object to be used for context.
     * @returns {InternalMessageBus} `InternalMessageBus instance`
     */
    off(channel, callback, context) {
        /* istanbul ignore next */ /* all cases covered with tests */
        if (validateChannel(channel)) {
            ee.off(channel, callback, context);
        }
        return this;
    }
    /**
     * emit(...params) - emits message on a certain channel. All channels that match the pattern are going to be notified.
     * If optional context is passed during .on/.once the function is going to be called with it.
     * @param {STRING} channel
     * @param {ANY} data
     * @returns {InternalMessageBus} `InternalMessageBus instance`
     */
    emit(channel, ...data) {
        /* istanbul ignore next */ /* all cases covered with tests */
        if (channel.match(/\*/gim)) {
            throw new Error("You cannot post to all.");
        }
        /* istanbul ignore next */ /* all cases covered with tests */
        if (validateChannel(channel)) {
            let subChannel = channel.split(".");
            let mainChannel = subChannel.shift();
            let tempChannels = subChannel
                .reduce((a, b, i) => {
                a.push(`${a[i]}.${b}`) && a;
                return a;
            }, [mainChannel]);
            let allChannels = [].concat(tempChannels.map(x => `${x}.*`), tempChannels.map(x => `${x}*`), [channel]);
            let msg = { channel: mainChannel, subChannel, data };
            allChannels.forEach(x => setTimeout(() => ee.emit(x, msg), 0));
        }
        return this;
    }
    /**
     * listeners(...params) - returns all listeners per provided channel.
     * @param {STRING} channel
     * @returns {InternalMessageBusListener[]} `InternalMessageBusListener[]`
     */
    listeners(channel) {
        /* istanbul ignore next */ /* all cases covered with tests */
        if (validateChannel(channel)) {
            return ee.listeners(channel);
        }
        /* istanbul ignore next */ /* all cases covered with tests */
        return [];
    }
}
//# sourceMappingURL=InternalMessageBus.js.map